<template>
    <v-container fluid class="grey lighten-5 pa-0">
        <v-row no-gutters>
            <v-navigation-drawer
                v-model="filterDrawer"
                :temporary="isMobilePort"
                :mobile-breakpoint="600"
                app
                clipped
                :width="isMobilePort ? '100%' : 360">
                <filters
                    :filters="filters"
                    :userProfileType="userProfileType"
                    @close="filterDrawer = false"
                    @apply-filters="applyFilters"
                    :default-filters="activeFilters"
                    :apply-instant-filters="!isMobilePort"
                    :key="activeFilters.tags ? activeFilters.tags.join(',') : ''" />
            </v-navigation-drawer>

            <!-- Main Content -->
            <div style="width: 100%">
                <!-- Top Bar -->
                <notes-header
                    @onPrintClick="onPrintClick"
                    :pagination="pagination"
                    :sectionsCount="totalSectionCount"
                ></notes-header>

                <v-container v-show="!anyFetching" fluid class="content-container container-height"
                    :class="!isMobilePort ? 'px-5' : ''">
                    <v-overlay :value="anyFetching" absolute>
                        <v-progress-circular indeterminate size="64"/>
                    </v-overlay>
                    <v-row align="center" justify="space-between">
                        <v-col :cols="isMobilePort ? 12 : 6">
                            <count-message
                                :currentSection="currentSection"
                                :totalSectionCount="totalSectionCount"
                                :totalNotes="totalNotesCount"
                            />
                        </v-col>
                        <v-col :class="{
                            'd-flex justify-end': !isMobilePort
                        }">
                            <v-btn
                                v-if="isMobilePort"
                                block
                                class="mb-5"
                                outlined
                                @click="filterDrawer = true">
                                <v-icon left>mdi-filter-variant</v-icon>
                                Filters
                            </v-btn>
                            <v-select
                                v-model="selectedGroupBy"
                                :items="groupByOptions"
                                item-text="text"
                                item-value="value"
                                label="Group by"
                                :block="isMobilePort"
                                :class="isMobilePort ? 'mb-5' : 'mr-2'"
                                dense
                                hide-details
                                outlined
                                :style="{ maxWidth: isMobilePort ? '100%' : '200px' }"
                                :menu-props="{ bottom: true, offsetY: true }"
                                @change="getResults" />
                            <v-select
                                v-model="selectedSort"
                                :items="sortOptions"
                                item-text="text"
                                item-value="value"
                                label="Sort by"
                                :block="isMobilePort"
                                :class="isMobilePort ? 'mb-5' : ''"
                                dense
                                hide-details
                                outlined
                                :style="{ maxWidth: isMobilePort ? '100%' : '200px' }"
                                :menu-props="{ bottom: true, offsetY: true }"
                                @change="getResults" />
                        </v-col>
                    </v-row>
                    <!-- Active filter chips -->
                    <active-filter-chips
                        :is-loading="anyFetching"
                        class="mb-6" :filters="filters"
                        :active-filters="activeFilters"
                        @clear-group="clearGroupFilter"
                        @remove-single-filter="removeSingleFilter" />
                    <!-- Content Sections -->
                    <v-row v-if="isContentView">
                        <v-col>
                            <default-content-section-controller
                                v-if="contentSections.length"
                                @sectionSortOrderChanged="sectionSortOrderChanged"
                                highlightType="add-note"
                                :content-sections="contentSections"
                                :is-notes-section-expanded="true" />
                        </v-col>
                    </v-row>
                    <v-row v-if="this.isTitleView">
                        <v-col>
                            <v-divider></v-divider>
                            <notes-not-found v-if="!isLoadingTitleContent && !titles.length"></notes-not-found>
                            <v-expansion-panels v-model="activeTitlePanel" accordion class="notes-expansion-panel-new">
                                <v-expansion-panel v-for="(title, i) in titles" :key="i" class="bg-transparent elevation-0">
                                    <v-expansion-panel-header hide-actions class="title-expansion-header" :class="isMobilePort ? 'px-0 py-4' : ''"
                                        style="gap: 10px;">
                                        <v-icon class="text--secondary">
                                            {{ activeTitlePanel === i ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                                        </v-icon>
                                        <graphic :displayOnlyTopicIcon="true" :additionalClasses="'d-flex'" :title="title"
                                            thumbnail width="24px">
                                        </graphic>
                                        <div :style="isMobilePort ? 'display: contents' : ''"><b>{{ title.display_title }}</b></div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <loading v-if="isLoadingTitleContent && activeTitleId === title.document_id" class="py-2">
                                            <span class="text-h6 font-weight-medium">Loading your notes...</span>
                                        </loading>
                                        <count-message
                                            class="py-3"
                                            v-if="!isLoadingTitleContent && activeTitleId === title.document_id"
                                            :currentSection="titleContentSections[title.document_id]?.length"
                                            :totalSectionCount="pagination.total"
                                            :totalNotes="getSectionNoteBookmarksCount"
                                        />
                                        <default-content-section-controller
                                            v-if="!isLoadingTitleContent
                                            && activeTitleId === title.document_id
                                            && titleContentSections[title.document_id]?.length"
                                            :content-sections="titleContentSections[title.document_id]"
                                            @sectionSortOrderChanged="sectionSortOrderChanged"
                                            highlightType="add-note"
                                            :is-notes-section-expanded="true"
                                            :registerDialogs="activeTitleId === title.document_id ? true : false"/>
                                        <notes-not-found v-if="!isLoadingTitleContent
                                            && activeTitleId === title.document_id
                                            && !titleContentSections[title.document_id]?.length"></notes-not-found>
                                        <template
                                            v-if="!isLoadingTitleContent
                                            && activeTitleId === title.document_id
                                            && titleContentSections[title.document_id]?.length < pagination.total">
                                            <show-next :isMoreLoading="isMoreLoading" :showNext="showNext" @loadMore="loadMore"/>
                                        </template>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                            <v-divider></v-divider>
                        </v-col>
                    </v-row>
                    <template v-if="isContentView && contentSections.length === 0">
                        <loading v-if="anyFetching">
                            <span class="text-h6 font-weight-medium">Loading your notes...</span>
                        </loading>
                        <notes-not-found v-else-if="pagination.total === 0"/>
                    </template>
                </v-container>
                <v-container v-if="anyFetching" fluid class="content-container container-height">
                    <loading>
                        <span class="text-h6 font-weight-medium">Loading your notes...</span>
                    </loading>
                </v-container>
                <template v-if="contentSections.length < pagination.total && isContentView">
                    <show-next :isMoreLoading="isMoreLoading" :showNext="showNext" @loadMore="loadMore"/>
                </template>
            </div>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import Filters from "./filters.vue";
import Mobile from '../../inc/mixins/mobile';
import { ContentSectionApi } from "../../../api/ContentSectionApi";
import DefaultContentSectionController from "../../content-section/DefaultContentSectionController.vue";
import ActiveFilterChips from "./ActiveFilterChips.vue";
import { NotesPageApi } from "../../../api/NotesPageApi";
import { defaultFilters } from "./defaultFilters";
import Loading from "../../content/partials/loading.vue";
import Graphic from "../../titles/cover/Graphic.vue";
import NotesHeader from './NotesHeader.vue';
import CountMessage from './CountMessage.vue';
import ShowNext from './ShowNext.vue';
import NotesNotFound from './NotesNotFound.vue';
import {UrlUtil} from "../../../util/UrlUtil";

export default {
    components: {
        Graphic,
        DefaultContentSectionController,
        Filters,
        ActiveFilterChips,
        Loading,
        NotesHeader,
        CountMessage,
        ShowNext,
        NotesNotFound
    },
    mixins: [Mobile],
    data() {
        const sectionViewConst = this.$getConst('sectionView');
        const titleViewConst = this.$getConst('titleView');
        const notesSortField = this.$getConst('notes').sortDateField;

        return {
            titlesNotesTotal: 0,
            filterDrawer: true,
            isMoreLoading: false,
            showFilters: false,
            contentSections: [],
            activeFilters: defaultFilters(),
            filters: {},
            pagination: { total: 0, offset: 0, limit: 20 },
            notesSortField,
            sort: [{ field: notesSortField, direction: 'DESC' }],
            userProfileType: '',
            selectedSort: 'newest',
            selectedGroupBy: sectionViewConst,
            titles: [],
            sortOptions: [
                { text: 'Newest Activity First', value: 'newest' },
                { text: 'Oldest Activity First', value: 'oldest' },
                { text: 'Section Number (Ascending)', value: 'section' }
            ],
            groupByOptions: [
                { text: 'Section', value: sectionViewConst },
                { text: 'Title and Section', value: titleViewConst },
            ],
            activeTitlePanel: null,
            isLoadingTitleContent: false,
            activeTitleId: null,
            sectionViewConst,
            titleViewConst,
            titleSectionCount: 0,
            titlesNotesCount: 0,
            titleContentSections: {},
            fetchCount: 0,
        };
    },
    computed: {
        ...mapGetters('NotesBookmarksModule', [
            'getSectionNoteBookmarksCount',
            'getTitlesNoteBookmarksCount'
        ]),
        showNext() {
            const contentSections = this.isTitleView ? this.titleContentSections[this.activeTitleId] : this.contentSections;
            const remaining = this.pagination.total - contentSections.length;
            return remaining > this.pagination.limit
                ? this.pagination.limit
                : remaining;
        },
        isTitleView() {
            return this.selectedGroupBy === this.titleViewConst;
        },
        isContentView() {
            return this.selectedGroupBy === this.sectionViewConst;
        },
        currentSection() {
            return this.isTitleView ? 0 : this.contentSections.length;
        },
        totalSectionCount() {
            return this.isTitleView ? this.titleSectionCount : this.pagination.total;
        },
        totalNotesCount() {
            return this.isTitleView ? this.getTitlesNoteBookmarksCount : this.getSectionNoteBookmarksCount;
        },
        anyFetching() {
            return this.fetchCount > 0;
        }
    },
    watch: {
        isMobilePort(val) {
            // Optionally close drawer when switching to mobile
            if (val) {
                this.filterDrawer = false;
            }
        },
        activeFilters: {
            deep: true,
            handler() {
                this.resetPagination();
                this.updateAndGetContentSections();
            }
        },
        activeTitlePanel(newVal) {
            if (newVal !== null && this.titles[newVal]) {
                this.resetPagination();
                this.onPanelExpand(this.titles[newVal]);
            }
        }
    },
    mounted() {
        this.getPageInfo();
        this.getResults();

        const urlParams = new URLSearchParams(window.location.search);
        const tagIdFromUrl = urlParams.get('tagId');

        if (tagIdFromUrl && /^\d+$/.test(tagIdFromUrl)) {
            const tagIdAsNumber = parseInt(tagIdFromUrl, 10);

            if (!Array.isArray(this.activeFilters.tags)) {
                this.activeFilters.tags = [];
            }

            if (!this.activeFilters.tags.includes(tagIdAsNumber)) {
                this.activeFilters.tags.push(tagIdAsNumber);

                this.updateAndGetContentSections();
            }
        }
    },
    methods: {
        async getResults() {
            this.fetchCount++;

            try {
                if (this.isContentView) {
                    await this.getContentSections(false, false);
                } else if (this.isTitleView) {
                    this.resetActiveTitleInfo();
                    await this.getGroupedByTitlesInfo();
                }
            } catch (err) {
                console.error("Error fetching results", err);
            } finally {
                this.fetchCount--;
            }
        },
        ...mapActions('NotesBookmarksModule', [
            'updateNoteBookmarksCount',
        ]),
        sectionSortOrderChanged(event) {
            let existent = localStorage.getItem('section_notes_sort_order_change');
            if (existent) {
                existent = JSON.parse(existent);
            } else {
                existent = {};
            }
            existent[event.sectionId] = event.sortOrder;
            localStorage.setItem('section_notes_sort_order_change', JSON.stringify(existent));
        },
        onPrintClick() {
            const qp = this.getQueryParams();

            localStorage.setItem('print_preview_query_params', JSON.stringify(qp));

            const printUrl = Routing.generate('notes_print_preview', qp);
            window.open(printUrl, '_blank');
        },
        async getPageInfo() {
            try {
                const response = await NotesPageApi.getPageInfo();
                if (response.filterOptions) {
                    this.filters = response.filterOptions;
                    this.userProfileType = response.userProfileType;
                }
            } catch (err) {
                //TODO:: do something?
            }
        },
        async getGroupedByTitlesInfo() {
            try {
                const queryParams = this.getQueryParams();
                const response = await NotesPageApi.getGroupedByTitlesInfo(queryParams);
                this.titles = response.titles;
                this.titleSectionCount = response.sectionsCount;
                this.titlesNotesCount = response.notesCount;

                this.updateNoteBookmarksCount({
                    type: 'titles',
                    count: this.titlesNotesCount
                });
            } catch (err) {
                // TODO: What to do?
            }
        },
        triggerSortingChange() {
            this.getContentSections(false, true);
        },
        async getContentSections(isLoadMore = false, sortChange = false) {
            if (!isLoadMore) {
                this.fetchCount++;
                this.contentSections = [];
            }
            if (isLoadMore) {
                this.fetchCount++;
                this.isMoreLoading = true;
            }

            try {
                if (!isLoadMore) {
                    // Move clearing here to avoid flickering in UI
                    if (this.isContentView && this.anyFetching) {
                        this.contentSections = [];
                    } else if (this.isTitleView && this.activeTitleId) {
                        this.$set(this.titleContentSections, this.activeTitleId, []);
                    }
                }

                const baseParams = this.getQueryParams(sortChange);
                const queryParams = {
                    ...baseParams,
                    ...(this.activeTitleId && this.isTitleView ? { titles: [this.activeTitleId] } : {})
                };

                const response = await ContentSectionApi.queryByUserNotes(queryParams);
                const { items, ...pagination } = response.pagination;

                this.pagination = pagination;

                this.updateNoteBookmarksCount({
                    type: this.isContentView ? 'section' : 'titles',
                    count: response.totalNotes
                });

                this.setContentSections(isLoadMore, items);
            } catch (err) {
                if (!axios.isCancel(err)) {
                    console.error("Error loading content sections", err);
                }
            } finally {
                this.fetchCount--;
            }
        },
        removeSingleFilter({ key, value }) {
            const current = this.activeFilters[key];
            if (Array.isArray(current)) {
                this.activeFilters[key] = current.filter(v => v !== value);
            } else {
                this.activeFilters[key] = null;
            }
        },
        loadMore() {
            const contentSections = this.isTitleView ? this.titleContentSections[this.activeTitleId] : this.contentSections;
            if (contentSections.length < this.pagination.total) {
                this.pagination.offset = this.pagination.offset + this.pagination.limit;
                this.getContentSections(true);
            }
        },
        clearGroupFilter(key) {
            this.activeFilters[key] = Array.isArray(this.activeFilters[key]) ? [] : null
        },
        applyFilters(filters = this.activeFilters) {
            this.activeFilters = filters;
            this.getResults();
        },
        resetPagination() {
            this.pagination = { total: 0, offset: 0, limit: 20 };
        },
        resetActiveTitleInfo() {
            this.activeTitlePanel = null;
            this.activeTitleId = null;
            this.titleContentSections = {};
        },
        getQueryParams(sortChange) {

            // TODO: why is this multidimensional?
            if ('newest' === this.selectedSort) {
                this.sort = [{ field: this.notesSortField, direction: 'DESC' }];
            } else if ('oldest' === this.selectedSort) {
                this.sort = [{ field: this.notesSortField, direction: 'ASC' }];
            } else {
                this.sort = [{ field: 'section_title', direction: 'ASC' }];
            }

            const qp = UrlUtil.buildQueryParams(this.activeFilters);

            return {
                ...qp,
                groupBy: this.selectedGroupBy,
                offset: sortChange ? 0 : this.pagination.offset,
                limit: sortChange ? 20 : this.pagination.limit,
                sort: this.sort,
            };
        },
        async onPanelExpand(title) {
            this.activeTitleId = title?.document_id;
            this.isLoadingTitleContent = true;
            await this.getContentSections();
            this.isLoadingTitleContent = false;
        },
        async setContentSections(isLoadMore, items) {
            const isTitleView = this.isTitleView;

            if (isTitleView) {
                const titleId = this.activeTitleId;
                const existing = this.titleContentSections[titleId] || [];
                const updated = isLoadMore ? [...existing, ...items] : [...items];
                this.$set(this.titleContentSections, titleId, updated);
            } else {
                this.contentSections = isLoadMore
                    ? [...this.contentSections, ...items]
                    : [...items];
            }

            if (isLoadMore) {
                this.isMoreLoading = false;
            }
        }
    },
    created() {
        localStorage.setItem('section_notes_sort_order_change', JSON.stringify({}));
        this.updateAndGetContentSections = _.debounce(this.getResults, 500);

        if (!this.activeFilters.tags) {
            this.$set(this.activeFilters, 'tags', []);
        }

        if (this.isMobilePort) {
            this.filterDrawer = false;
        }
    }
};
</script>

<style lang="scss" scoped>
    .v-expansion-panel-header>:not(.v-expansion-panel-header__icon) {
        flex: none !important;
    }

    .notes-expansion-panel-new {
        .v-expansion-panel-content__wrap {
            padding: 0 !important;
        }
    }

    .outlined-except-top-left {
        border: 1px solid #E0E0E0;
        border-top: none;
        border-left: none;
    }

    .v-expansion-panels {
        z-index: unset;
    }
    .v-expansion-panel {
        background-color: unset !important;
    }
</style>